import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { X, Plus, Trash2, MapPin, Users, MoreHorizontal, Camera } from 'lucide-react';
import { Combobox } from './ui/combobox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose
} from './ui/dialog';
import { CommanServices } from '@/services/commanServices';
import AttachmentPreviewModal from './AttachmentPreviewModal';
import AuthenticatedMedia from './AuthenticatedMedia';
import DuplicateChecker from './DuplicateChecker';
import MyGoogleMap from './MyGoogleMap';
import DepartmentSelector from './DepartmentSelector';

let lastLocationId;

const LocationHierarchyManager = ({
  value = {
    location_type: '',
    location_value: '',
    details: [],
    gps: null,
    departments: []
  },
  onChange,
  className = "",
  disabled = false,
  focusChipId = null,  // ID of the chip to focus/highlight
  onValidationChange = null,  // Callback to notify parent about validation state
  formData = {},
  setAttachments = () => { },
  attachments,
  selectedZone,
  setSelectedTreeNodeId = () => { },
  displayedZones = [],
  setIsVailid = () => { },
  isVailid,
  gpsPicker = null  // GPS map picker (ZoneGpsPicker) rendered below the status select
}) => {
  const [newDetailInputs, setNewDetailInputs] = useState({}); // Track input state for each detail
  const [editingDetails, setEditingDetails] = useState(new Set()); // Track which details are in edit mode
  const [openPopovers, setOpenPopovers] = useState({}); // Track which popovers are open

  // Unified suggestions state
  const [typesSuggestions, setTypesSuggestions] = React.useState([]);
  const [valuesSuggestions, setValuesSuggestions] = React.useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = React.useState(false);
  const [previewAttechment, setPreviewAttechment] = useState([]);
  const [isEnterCoordsOpen, setIsEnterCoordsOpen] = useState(false);
  const [enteredLat, setEnteredLat] = useState('');
  const [enteredLng, setEnteredLng] = useState('');
  // Currently selected attachment for preview modal (either local File object or remote preview)
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [nodeIds, setNodeIds] = useState([]);
  const [isValidOptionalNode, setIsValidOptionalNode] = useState(false);
  const [isValidOptionalNodeTree, setIsValidOptionalNodeTree] = useState(false);
  const [isTreeNodeNames, setIsTreeNodeNames] = useState([]);

  // Fetch unified suggestions on component mount
  React.useEffect(() => {
    const fetchUnifiedSuggestions = async () => {
      setIsLoadingSuggestions(true);
      try {

        // Fetch unified types and values in parallel
        const [typesResponse, valuesResponse] = await Promise.all([
          CommanServices.locationsType(),
          CommanServices.locationsValue()
        ]);

        if (typesResponse.status && valuesResponse.status) {
          const typesData = await typesResponse.data;
          const valuesData = await valuesResponse.data;

          // Convert to combobox format
          const typesOptions = typesData.map(type => ({ value: type, label: type }));
          const valuesOptions = valuesData.map(value => ({ value: value, label: value }));

          setTypesSuggestions(typesOptions);
          setValuesSuggestions(valuesOptions);

        } else {
          console.error('❌ Failed to fetch unified suggestions');
          // Fallback to basic suggestions
          setFallbackSuggestions();
        }
      } catch (error) {
        console.error('❌ Error fetching unified suggestions:', error);
        setFallbackSuggestions();
      } finally {
        setIsLoadingSuggestions(false);
      }
    };

    fetchUnifiedSuggestions();
  }, []);

  // Fallback suggestions if fails
  const setFallbackSuggestions = () => {
    const fallbackTypes = [
      { value: 'Building', label: 'Building' },
      { value: 'Floor', label: 'Floor' },
      { value: 'Room', label: 'Room' },
      { value: 'Corridor', label: 'Corridor' },
      { value: 'Wing', label: 'Wing' },
      { value: 'Section', label: 'Section' }
    ];

    const fallbackValues = [
      { value: 'Main Hospital', label: 'Main Hospital' },
      { value: '1', label: '1' },
      { value: '2', label: '2' },
      { value: 'A', label: 'A' },
      { value: 'B', label: 'B' },
      { value: '101', label: '101' }
    ];

    setTypesSuggestions(fallbackTypes);
    setValuesSuggestions(fallbackValues);
  };

  const departments = [
    { value: 'Emergency', label: 'Emergency' },
    { value: 'ICU', label: 'ICU' },
    { value: 'Radiology', label: 'Radiology' },
    { value: 'Cardiology', label: 'Cardiology' },
    { value: 'Surgery', label: 'Surgery' }
  ];

  // Check for unfinished rows (Save Guard)
  const hasUnfinishedRows = () => {
    // Check if there are unsaved detail inputs
    const hasUnsavedDetailInputs = Object.keys(newDetailInputs).some(key => {
      const inputValue = newDetailInputs[key];
      return inputValue && inputValue.trim().length > 0;
    });

    // Check if any details are in editing mode
    const hasDetailsInEditMode = editingDetails.size > 0;

    const unfinishedState = hasUnsavedDetailInputs || hasDetailsInEditMode;

    // Notify parent component about validation state
    if (onValidationChange) {
      onValidationChange({
        hasUnfinishedRows: unfinishedState,
        unfinishedRowsMessage: unfinishedState ?
          "Please complete or cancel all detail rows before saving." : null
      });
    }

    return unfinishedState;
  };

  // Run validation check whenever relevant state changes
  React.useEffect(() => {
    hasUnfinishedRows();
  }, [newDetailInputs, editingDetails, onValidationChange]);

  // Helpers: find node by id within the hierarchy and update a specific node field.
  const findNodeById = (node, id) => {
    if (!node || !id) return null;
    if (node.id && node.id === id) return node;
    const children = node.details || node.subdetails || [];
    for (const child of children) {
      const found = findNodeById(child, id);
      if (found) return found;
    }
    return null;
  };

  const updateNodeField = (nodeId, fieldName, newValue) => {
    // Deep clone hierarchy to avoid mutating props
    const updatedHierarchy = JSON.parse(JSON.stringify(value || {}));
    const target = findNodeById(updatedHierarchy, nodeId);

    if (target) {
      // Support both shapes: node may use { type, value } or { location_type, location_value }
      if (fieldName === 'location_type') {
        if (typeof target.type !== 'undefined') target.type = newValue;
        else target.location_type = newValue;
      } else if (fieldName === 'location_value') {
        if (typeof target.value !== 'undefined') target.value = newValue;
        else target.location_value = newValue;
      } else {
        // generic assign
        target[fieldName] = newValue;
      }

      onChange(updatedHierarchy);
      return;
    }

    // fallback: update top-level fields if node not found
    onChange({
      ...value,
      [fieldName]: newValue
    });
  };

  // Update primary location type/value.
  // If an explicit editing node is provided via formData._hierarchy_meta.editing_node_id (or backend target),
  // update that node inside the hierarchy instead of the top-level root.
  const updatePrimaryLocation = (field, newValue) => {
    const editingNodeId = formData?._hierarchy_meta?.editing_node_id || formData?._hierarchy_meta?.target_chip_id || null;
    if (editingNodeId) {
      updateNodeField(editingNodeId, field, newValue);
      return;
    }

    // default behavior: update root/top-level
    onChange({
      ...value,
      [field]: newValue
    });
  };

  // Handle toggling a department's primary status for the current editing node or root.
  // Stores primary dept IDs in `departments_primary` field of the node.
  const handlePrimaryToggle = (deptId, isPrimary) => {
    const editingNodeId = formData?._hierarchy_meta?.editing_node_id || formData?._hierarchy_meta?.target_chip_id || null;
    const updatedHierarchy = JSON.parse(JSON.stringify(value || {}));

    const applyToggle = (node) => {
      const currentPrimary = node.departments_primary || [];
      if (isPrimary) {
        // add if not already present
        if (!currentPrimary.includes(deptId)) {
          node.departments_primary = [...currentPrimary, deptId];
        }
      } else {
        node.departments_primary = currentPrimary.filter(id => id !== deptId);
      }
    };

    if (editingNodeId) {
      const target = findNodeById(updatedHierarchy, editingNodeId);
      if (target) {
        applyToggle(target);
        onChange(updatedHierarchy);
        return;
      }
    }
    // fallback: update root
    applyToggle(updatedHierarchy);
    onChange(updatedHierarchy);
  };

  // Add a new detail
  const addDetail = () => {
    const detailTypeInput = newDetailInputs['detail_type'] || '';
    const detailValueInput = newDetailInputs['detail_value'] || '';


    if (!detailTypeInput.trim() || !detailValueInput.trim()) {
      return;
    }

    const newDetail = {
      id: (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `tmp-${Date.now()}`,
      type: detailTypeInput.trim(),
      value: detailValueInput.trim(),
      subdetails: [],
      gps: null,
      departments: [],
      isEditing: false
    };


    const updatedValue = JSON.parse(JSON.stringify(value || {}));
    updatedValue.details = [...(updatedValue.details || []), newDetail];


    onChange(updatedValue);

    // Clear inputs
    setNewDetailInputs({
      ...newDetailInputs,
      detail_type: '',
      detail_value: ''
    });

  };

  // Add a nested detail (child) under a specific detail
  const addNestedDetail = (detailIndex, path = []) => {
    const editKey = path.length > 0 ? `${detailIndex}-${path.join('-')}` : detailIndex.toString();

    const updatedData = JSON.parse(JSON.stringify(value || {}));
    let currentDetail;

    // Navigate to the correct detail
    if (path.length === 0) {
      currentDetail = updatedData.details[detailIndex];
    } else {
      currentDetail = updatedData.details[detailIndex];
      for (const index of path) {
        currentDetail = currentDetail.subdetails[index];
      }
    }

    let typeValue, valueValue;

    // Check if detail is in editing mode or already a chip
    if (currentDetail.isEditing) {
      // For editing mode: check inputs
      typeValue = newDetailInputs[`${editKey}-type`] || currentDetail.type || '';
      valueValue = newDetailInputs[`${editKey}-value`] || currentDetail.value || '';

      // Validate that current row has both type and value
      if (!typeValue.trim() || !valueValue.trim()) {
        alert('Please fill in both Detail type and Detail value before adding a nested detail.');
        return;
      }

      // Save the current editing row (convert to chip)
      currentDetail.type = typeValue.trim();
      currentDetail.value = valueValue.trim();
      currentDetail.isEditing = false;
    } else {
      // For chip mode: use existing values
      typeValue = currentDetail.type || '';
      valueValue = currentDetail.value || '';

      // Validate that current detail has both type and value
      if (!typeValue.trim() || !valueValue.trim()) {
        alert('This detail is missing type or value information.');
        return;
      }
    }

    // Second: Create a new child detail under the now-saved current detail
    const newChildDetail = {
      id: (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `tmp-${Date.now()}`,
      type: '',
      value: '',
      subdetails: [],
      gps: null,
      departments: [],
      isEditing: true
    };

    // Ensure subdetails array exists
    if (!currentDetail.subdetails) {
      currentDetail.subdetails = [];
    }

    currentDetail.subdetails.push(newChildDetail);
    onChange(updatedData);

    // Remove current detail from editing state (it's now a chip)
    setEditingDetails(prev => {
      const newSet = new Set(prev);
      newSet.delete(editKey);
      return newSet;
    });

    // Add the new child to editing state
    const newChildPath = [...path, currentDetail.subdetails.length - 1];
    const childEditKey = `${detailIndex}-${newChildPath.join('-')}`;

    setEditingDetails(prev => new Set([...prev, childEditKey]));

    // Clear parent inputs and focus on child
    setNewDetailInputs(prev => {
      const newInputs = { ...prev };
      delete newInputs[`${editKey}-type`];
      delete newInputs[`${editKey}-value`];
      return newInputs;
    });

    // Focus on the new child input
    setTimeout(() => {
      const childInput = document.querySelector(`input[data-focus-key="${childEditKey}-type"]`);
      childInput?.focus();
    }, 100);
  };

  // Peer functionality removed - only nested details needed

  // Save/confirm a detail (convert from editing to chip)
  const saveDetail = (detailIndex, path = []) => {
    const editKey = path.length > 0 ? `${detailIndex}-${path.join('-')}` : detailIndex.toString();
    const typeInput = newDetailInputs[`${editKey}-type`] || '';
    const valueInput = newDetailInputs[`${editKey}-value`] || '';

    if (!typeInput.trim() || !valueInput.trim()) return;

    const updatedData = JSON.parse(JSON.stringify(value || {}));
    let currentDetail;

    if (path.length === 0) {
      currentDetail = updatedData.details[detailIndex];
    } else {
      currentDetail = updatedData.details[detailIndex];
      for (let i = 0; i < path.length - 1; i++) {
        currentDetail = currentDetail.subdetails[path[i]];
      }
      currentDetail = currentDetail.subdetails[path[path.length - 1]];
    }

    currentDetail.type = typeInput.trim();
    currentDetail.value = valueInput.trim();
    currentDetail.isEditing = false;

    onChange(updatedData);

    // Remove from editing state
    setEditingDetails(prev => {
      const newSet = new Set(prev);
      newSet.delete(editKey);
      return newSet;
    });

    // Clear inputs
    setNewDetailInputs(prev => {
      const newInputs = { ...prev };
      delete newInputs[`${editKey}-type`];
      delete newInputs[`${editKey}-value`];
      return newInputs;
    });
  };

  // Handle Enter key navigation
  const handleKeyPress = (e, detailIndex, path, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const editKey = path.length > 0 ? `${detailIndex}-${path.join('-')}` : detailIndex.toString();

      if (field === 'type') {
        // Move to value field
        const valueInput = document.querySelector(`input[data-focus-key="${editKey}-value"]`);
        valueInput?.focus();
      } else if (field === 'value') {
        // Save the detail
        saveDetail(detailIndex, path);
      }
    }
  };

  // Remove a detail or sub-detail with confirmation
  const removeDetail = (detailIndex, path = []) => {
    const editKey = path.length > 0 ? `${detailIndex}-${path.join('-')}` : detailIndex.toString();

    const updatedData = JSON.parse(JSON.stringify(value || {}));
    let targetDetail;

    if (path.length === 0) {
      targetDetail = updatedData.details[detailIndex];
    } else {
      targetDetail = updatedData.details[detailIndex];
      for (const index of path.slice(0, -1)) {
        targetDetail = targetDetail.subdetails[index];
      }
      targetDetail = targetDetail.subdetails[path[path.length - 1]];
    }

    // Count total descendants for confirmation
    const countDescendants = (detail) => {
      let count = detail.subdetails?.length || 0;
      detail.subdetails?.forEach(sub => {
        count += countDescendants(sub);
      });
      return count;
    };

    const descendantCount = countDescendants(targetDetail);
    const confirmMessage = descendantCount > 0
      ? `Delete '${targetDetail.type}: ${targetDetail.value}' and ${descendantCount} nested detail${descendantCount > 1 ? 's' : ''}? This will also remove any GPS/Department overrides set on its descendants.`
      : targetDetail.type && targetDetail.value
        ? `Delete '${targetDetail.type}: ${targetDetail.value}'?`
        : `Delete '${newDetailInputs[`${editKey}-type`]}: ${newDetailInputs[`${editKey}-value`]}'?`;

    if (window.confirm(confirmMessage)) {
      if (path.length === 0) {
        updatedData.details.splice(detailIndex, 1);
      } else {
        let currentDetail = updatedData.details[detailIndex];
        for (let i = 0; i < path.length - 1; i++) {
          currentDetail = currentDetail.subdetails[path[i]];
        }
        currentDetail.subdetails.splice(path[path.length - 1], 1);
      }

      onChange(updatedData);

      // Clean up editing state if this detail was being edited
      setEditingDetails(prev => {
        const newSet = new Set(prev);
        newSet.delete(editKey);
        return newSet;
      });
      setNewDetailInputs(prev => {
        const newInputs = { ...prev };
        delete newInputs[`${editKey}-type`];
        delete newInputs[`${editKey}-value`];
        return newInputs;
      });
    }
  };

  // Handle detail input changes
  const handleDetailInputChange = (key, inputValue) => {
    const updatedInputs = {
      ...newDetailInputs,
      [key]: inputValue
    };
    setNewDetailInputs(updatedInputs);
  };

  // Helper to set GPS on editing node (if present) or root
  const setGPSForTarget = (gps) => {
    const editingNodeId = formData?._hierarchy_meta?.editing_node_id || formData?._hierarchy_meta?.target_chip_id || null;
    if (editingNodeId) {
      updateNodeField(editingNodeId, 'gps', gps);
    } else {
      onChange({
        ...value,
        gps
      });
    }
    alert(`GPS set to: ${gps.lat.toFixed(6)}, ${gps.lng.toFixed(6)}`);
  };

  // Use device geolocation (used by "Use My Device")
  const handleSetGPS = () => {
    if (navigator && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const gps = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setGPSForTarget(gps);
        },
        (err) => {
          console.warn('Geolocation failed, falling back to mock GPS', err);
          // Fallback mock GPS
          const mockGPS = {
            lat: 40.7128 + Math.random() * 0.01,
            lng: -74.0060 + Math.random() * 0.01
          };
          setGPSForTarget(mockGPS);
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );
    } else {
      // No geolocation available - fallback to mock
      const mockGPS = {
        lat: 40.7128 + Math.random() * 0.01,
        lng: -74.0060 + Math.random() * 0.01
      };
      setGPSForTarget(mockGPS);
    }
  };

  // Set GPS from user-entered coordinates (used by "Enter Coordinates" dialog)
  const handleSetGPSFromCoords = () => {
    const lat = parseFloat(enteredLat);
    const lng = parseFloat(enteredLng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      alert('Please enter valid decimal numbers for latitude and longitude.');
      return;
    }
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      alert('Latitude must be between -90 and 90. Longitude must be between -180 and 180.');
      return;
    }

    const gps = { lat, lng };
    setGPSForTarget(gps);

    // clear inputs after setting
    setEnteredLat('');
    setEnteredLng('');
    setIsEnterCoordsOpen(false);
  };

  // Mock department management for primary location
  const handleManageDepartments = () => {
    // Mock adding a random department
    const randomDept = departments[Math.floor(Math.random() * departments.length)];

    onChange({
      ...value,
      departments: [randomDept.value]
    });

    alert(`Department added: ${randomDept.value}`);
  };

  // Handle GPS setting for specific detail
  const handleDetailSetGPS = (detailIndex, path = []) => {
    const mockGPS = {
      lat: 40.7128 + Math.random() * 0.01,
      lng: -74.0060 + Math.random() * 0.01
    };

    const updatedData = JSON.parse(JSON.stringify(value || {}));
    let currentDetail;

    if (path.length === 0) {
      currentDetail = updatedData.details[detailIndex];
    } else {
      currentDetail = updatedData.details[detailIndex];
      for (const index of path) {
        currentDetail = currentDetail.subdetails[index];
      }
    }

    currentDetail.gps = mockGPS;
    onChange(updatedData);

    // Close popover
    const popoverKey = path.length > 0 ? `${detailIndex}-${path.join('-')}` : detailIndex.toString();
    setOpenPopovers(prev => ({ ...prev, [popoverKey]: false }));
  };

  // Handle department management for specific detail
  const handleDetailManageDepartments = (detailIndex, path = []) => {
    const randomDept = departments[Math.floor(Math.random() * departments.length)];

    const updatedData = JSON.parse(JSON.stringify(value || {}));
    let currentDetail;

    if (path.length === 0) {
      currentDetail = updatedData.details[detailIndex];
    } else {
      currentDetail = updatedData.details[detailIndex];
      for (const index of path) {
        currentDetail = currentDetail.subdetails[index];
      }
    }

    currentDetail.departments = [randomDept.value];
    onChange(updatedData);

    // Close popover
    const popoverKey = path.length > 0 ? `${detailIndex}-${path.join('-')}` : detailIndex.toString();
    setOpenPopovers(prev => ({ ...prev, [popoverKey]: false }));
  };

  // Get effective GPS with inheritance
  const getEffectiveGPS = (detail, parentGPS = null) => {
    if (detail.gps) return detail.gps;
    return parentGPS || value.gps;
  };

  // Get GPS for the currently edited/target node or fallback to root gps
  const getTargetGPS = () => {
    const editingNodeId = formData?._hierarchy_meta?.editing_node_id || formData?._hierarchy_meta?.target_chip_id || null;
    if (editingNodeId) {
      const node = findNodeById(value, editingNodeId);
      // Prefer node.gps if present; otherwise fall back to root GPS (inheritance resolution is best-effort)
      return node.gps;
    }
    return value.gps || null;
  };

  // Get effective departments with inheritance
  const getEffectiveDepartments = (detail, parentDepartments = []) => {
    if (detail.departments && detail.departments.length > 0) return detail.departments;
    return parentDepartments.length > 0 ? parentDepartments : value.departments;
  };

  const isValidChildTreeTypeValue = (type, value, where_edit, isChildNames = []) => {
    try {
      const ArrayTrees = isChildNames && isChildNames.length > 0 ? isChildNames : isTreeNodeNames
      const isValid = ArrayTrees.some(
        item =>
          item.type === type.toLowerCase() &&
          item.value === value
      );

      if (where_edit == "top") {
        setIsVailid(isValid)
      } else if (where_edit == "middle") {
        setIsValidOptionalNode(isValid);
      } else if (where_edit == "bottom") {
        setIsValidOptionalNodeTree(isValid);
      }
    } catch (error) {
      console.error(error);
      setIsValidOptionalNodeTree(false);
      setIsValidOptionalNode(false);
      setIsVailid(false);
    }
  };

  // Render detail with sub-details (like Events with Triggers)
  const renderDetail = (detail, detailIndex, path = [], parentGPS = null, parentDepartments = [], is_parent = false, dataTress = []) => {
    const isTopLevel = path.length === 0;
    const editKey = isTopLevel ? detailIndex.toString() : `${detailIndex}-${path.join('-')}`;
    const isEditing = detail.isEditing || editingDetails.has(editKey);
    const indentLevel = path.length;
    const effectiveGPS = getEffectiveGPS(detail, parentGPS);
    const effectiveDepartments = getEffectiveDepartments(detail, parentDepartments);
    const popoverKey = editKey;

    const DetailContent = () => (
      <>
        {isEditing ? (
          // Editing Mode - Input form (Before Save)
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-4">
              <div className='flex flex-col'>
                <Combobox
                  value={newDetailInputs[`${editKey}-type`] || detail.type || ''}
                  onValueChange={(value) => {
                    if (value.trim().toLowerCase() && newDetailInputs[`${editKey}-value`]) {
                      isValidChildTreeTypeValue(value, newDetailInputs[`${editKey}-value`], 'bottom', dataTress)
                    }
                    handleDetailInputChange(`${editKey}-type`, value);
                  }}
                  placeholder="Type"
                  options={typesSuggestions}
                  createLabel="Create type"
                  className="border-gray-300"
                  data-focus-key={`${editKey}-type`}

                />
              </div>
              <div className="flex gap-2">
                <Combobox
                  value={newDetailInputs[`${editKey}-value`] || detail.value || ''}
                  onValueChange={(value) => {
                    if (value && newDetailInputs[`${editKey}-type`]) {
                      isValidChildTreeTypeValue(newDetailInputs[`${editKey}-type`], value, "bottom", dataTress);
                    }
                    handleDetailInputChange(`${editKey}-value`, value)
                  }}
                  placeholder="Value"
                  options={valuesSuggestions}
                  createLabel="Create value"
                  className="border-gray-300 flex-1"
                  data-focus-key={`${editKey}-value`}

                />
                {/* Before Save: Show only Add Detail and Delete buttons */}
                <Button
                  onClick={() => saveDetail(detailIndex, path)}
                  disabled={!newDetailInputs[`${editKey}-type`]?.trim() || !newDetailInputs[`${editKey}-value`]?.trim() || isValidOptionalNodeTree}
                  size="sm"
                  variant="secondary"
                  className="px-2 text-xs shrink-0"
                  title="Save this detail"
                >
                  <Plus className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeDetail(detailIndex, path)}
                  className="h-6 w-6 p-0 hover:bg-red-100 shrink-0"
                  title="Delete"
                >
                  <Trash2 className="h-3 w-3 text-red-500" />
                </Button>
              </div>
            </div>
            {
              isValidOptionalNodeTree &&
              <div className='mt-1'>
                <div className='flex items-start text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2'>
                  <svg className="flex-shrink-0 mr-2 mt-0.5 h-4 w-4 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span>{`A location zones with this name and value already exists : "${newDetailInputs[`${editKey}-type`]}" : "${newDetailInputs[`${editKey}-value`]}". Please choose a different name and value.`}</span>
                </div>
              </div>
            }

          </div>
        ) : (
          // Chip Mode - Saved detail with full functionality (After Save)
          <div className="space-y-2">
            {is_parent && <div className="mt-3">
              <div className="space-y-2">
                {formData?._hierarchy_meta?.root_label ? (
                  (() => {
                    const isRootChip = formData._hierarchy_meta.root_chip_id === focusChipId;
                    return (
                      <div onClick={() => setSelectedTreeNodeId(formData._hierarchy_meta.root_chip_id)}>
                        <Badge
                          variant="outline"
                          className={`cursor-pointer hover:shadow-md transition-all duration-200 inline-flex items-center gap-2 px-3 py-2 text-sm font-medium ${isRootChip
                            ? 'bg-yellow-100 text-yellow-800 border-yellow-400 ring-2 ring-yellow-300 animate-pulse'
                            : 'bg-blue-100 text-blue-800 border-blue-300'
                            }`}
                        >
                          <MapPin className="w-3 h-3" />
                          {`${formData.location_hierarchy.location_type} : ${formData.location_hierarchy.location_value}`}
                        </Badge>

                      </div>
                    );
                  })()
                ) : value.location_value ? (
                  <Badge variant="outline" className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium bg-blue-100 text-blue-800 border-blue-300">
                    <MapPin className="w-3 h-3" />
                    {value.location_type}: {value.location_value}
                  </Badge>
                ) : null}
              </div>
            </div>}
            <div className="flex items-center gap-2">
              {
                selectedZone == null || focusChipId && detail.id == focusChipId ?
                  <Button
                    onClick={() => { addNestedDetail(detailIndex, path); }}
                    size="sm"
                    variant="secondary"
                    className={`px-2 text-xs cursor-pointer`}
                    title="Add nested detail"
                  >
                    <span className="text-xs">↳</span>
                  </Button>
                  :
                  <div className={`px-2 text-xs cursor-default`}>
                    <span className="text-xs">-</span>
                  </div>
              }

              <Popover
                open={openPopovers[popoverKey] || false}
              // onOpenChange={(open) => setOpenPopovers(prev => ({...prev, [popoverKey]: open}))}
              >
                <PopoverTrigger asChild onClick={() => {
                  if (selectedZone !== null && detail && detail.id && nodeIds.includes(detail.id)) {
                    setSelectedTreeNodeId(detail.id);
                  }
                }}>
                  <Badge
                    variant="outline"
                    className={`cursor-pointer hover:shadow-md transition-all duration-200 flex items-center gap-2 ${focusChipId && detail.id === focusChipId
                      ? 'bg-yellow-100 text-yellow-800 border-yellow-400 ring-2 ring-yellow-300 px-3 py-2 text-sm font-medium animate-pulse'
                      : isTopLevel
                        ? 'bg-blue-100 text-blue-800 border-blue-300 px-3 py-2 text-sm font-medium'
                        : 'bg-gray-100 text-gray-700 border-gray-300 px-2 py-1 text-xs'
                      }`}
                  >
                    <MapPin className={`${isTopLevel ? 'w-3 h-3' : 'w-2 h-2'}`} />
                    📍 {detail.type}: {detail.value}
                    {effectiveGPS && <span className="text-green-600">🌍</span>}
                    {effectiveDepartments.length > 0 && <span className="text-purple-600">👥</span>}
                  </Badge>
                </PopoverTrigger>

              </Popover>

              {!nodeIds.includes(detail.id) && <Button
                variant="ghost"
                size="sm"
                onClick={() => removeDetail(detailIndex, path)}
                className="h-6 w-6 p-0 hover:bg-red-100"
                title="Delete"
              >
                <Trash2 className="h-3 w-3 text-red-500" />
              </Button>}
            </div>
          </div>
        )}

        {/* Render Sub-details Recursively */}
        {detail.subdetails && detail.subdetails.map((subdetail, subIndex) =>
          renderDetail(subdetail, detailIndex, [...path, subIndex], effectiveGPS, effectiveDepartments, '', dataTress)
        )}
      </>
    );

    return (
      <div key={detail.id} className={`${isTopLevel ? 'border rounded-lg p-4 bg-gray-50' : 'mt-2'}`}>
        {isTopLevel || isEditing ? (
          // Top-level details or editing rows: no hierarchy connectors
          <DetailContent />
        ) : (
          // Saved nested details: show hierarchy with indentation only
          <div className="flex items-start" style={{ paddingLeft: `${indentLevel * 20}px` }}>
            <div className="flex-1">
              <DetailContent />
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const MAX_VIDEO_SIZE = 20 * 1024 * 1024;
    const MAX_FILE_SIZE = 20 * 1024 * 1024;

    const rejected = files.filter((file) => {
      const isVideo = file.type && file.type.startsWith("video/");
      const limit = isVideo ? MAX_VIDEO_SIZE : MAX_FILE_SIZE;
      return file.size > limit;
    });

    if (rejected.length > 0) {
      const lines = rejected
        .map((f) => {
          const isVideo = f.type && f.type.startsWith("video/");
          const label = "20 MB";
          return `${f.name} (${(f.size / (1024 * 1024)).toFixed(1)} MB > ${label})`;
        })
        .join("\n");
      alert(`Some files were not added because they exceed the size limit:\n\n${lines}`);
      e.target.value = "";
      // still allow valid files to be added
    }

    const accepted = files.filter((f) => !rejected.includes(f));
    if (accepted.length > 0) {
      setAttachments((prev) => [...prev, ...accepted]);
    }
    e.target.value = "";
  };


  const removeFile = (index) => {
    // revoke the blob URL to free memory
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const fetchPreviews = async (id) => {
    const previews = [];
    for (const item of selectedZone.attachments) {
      try {
        const response = await CommanServices.imagePreview(item.key);
        if (response.status) {
          const data = await response.data;
          previews.push({
            ...data,
            id: item.id || item.key,
            location_zone_id: selectedZone?.id
          });
        } else {
          console.error('Failed to fetch preview for', item.key, response.message);
        }
      } catch (error) {
        console.error('Error fetching preview for', item.key, error);
      }
    }
    if (id == lastLocationId) {
      setPreviewAttechment(previews);
    }
  };

  function getAllTypesAndValues(data, subdetails = false) {
    const result = new Map(); // key = type|value

    function add(type, value, id) {
      if (!type || value == null) return;

      const key = `${type.toLowerCase()}|${value}|${id}`;
      if (!result.has(key)) {
        result.set(key, {
          type: type.toLowerCase(),
          value: value,
          id: id
        });
      }
    }

    function traverse(obj) {
      if (!obj || typeof obj !== "object") return;

      // add location_type + location_value
      if (obj.location_type && obj.location_value) {
        add(obj.location_type, obj.location_value, obj.id);
      }

      // add type + value
      if (obj.type && obj.value) {
        add(obj.type, obj.value, obj.id);
      }

      // traverse nested arrays
      if (Array.isArray(obj.details)) {
        obj.details.forEach(traverse);
      }

      if (Array.isArray(obj.subdetails) && subdetails) {
        obj.subdetails.forEach(traverse);
      }
    }

    traverse(data);

    return Array.from(result.values());
  }

  const removeFileAllreadyAdded = async (locationZoneId, attachmentId) => {
    try {
      const payload = {
        locationZoneId,
        attachmentId
      }
      const strPayload = JSON.stringify(payload)
      const res = await CommanServices.deleteLocationZonesAttachment(strPayload)

      if (res && res.status) {
        setPreviewAttechment(prev => prev.filter(p => {
          const pid = p?.id ? String(p.id) : null;
          const pkey = p?.key ? String(p.key) : null;
          return !((pid && pid === String(attachmentId)) || (pkey && pkey === String(attachmentId)));
        }));
      } else {
        const msg = (json && json.message) ? json.message : 'Failed to remove attachment';
        alert(msg);
      }
    } catch (error) {
      console.error('Error deleting attachment', error);
      alert('Error deleting attachment');
    }
  }

  useEffect(() => {
    lastLocationId = selectedZone?.id
    if (selectedZone?.attachments.length > 0) {
      fetchPreviews(selectedZone?.id);
    } else {
      setPreviewAttechment([]);
    }
    setNewDetailInputs({});
    setEditingDetails(new Set());
    setIsValidOptionalNode(false);
    setIsValidOptionalNodeTree(false);
    setIsVailid(false);
  }, [selectedZone]);

  useEffect(() => {
    const ids = displayedZones.map((item) => item.id);
    setNodeIds(ids);
  }, [JSON.stringify(displayedZones)]);

  useEffect(() => {
    if (selectedZone) {
      if (value?.id == focusChipId) {
        const valueTypeArray = getAllTypesAndValues(value, true);
        setIsTreeNodeNames(valueTypeArray);
      } else {
        let secondvalueTypeArray = []
        value.details.forEach((detail) => {
          const valueTypeArray = getAllTypesAndValues(detail, true);
          if (detail.id == focusChipId) {
            secondvalueTypeArray = getAllTypesAndValues(value, false);
            const dataTress = [...valueTypeArray, ...secondvalueTypeArray].concat([{ type: value.location_type.toLowerCase(), value: value.location_value, id: value.id }])
            const isValid = dataTress.some(
              item => item.id == focusChipId
            );
            if (isValid) {
              setIsTreeNodeNames(dataTress);
            }
          } else {
            const dataTress = valueTypeArray.concat([{ type: value.location_type.toLowerCase(), value: value.location_value, id: value.id }])
            const isValid = dataTress.some(
              item => item.id == focusChipId
            );
            if (isValid) {
              setIsTreeNodeNames(dataTress);
            }
          }
        })

      }
    } else {
      const valueTypeArray = getAllTypesAndValues(value, true);
      setIsTreeNodeNames(valueTypeArray);
    }
  }, [JSON.stringify(value), selectedZone])



  return (
    <>
      <div className={`space-y-4 ${className}`}>
        {/* Primary Location Type & Value (like Incident Type) */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Location Type <span className="text-red-500">*</span>
            </label>
            <Combobox
              value={(() => {
                const editingNodeId = formData?._hierarchy_meta?.editing_node_id || formData?._hierarchy_meta?.target_chip_id || null;
                if (editingNodeId) {
                  const node = findNodeById(value, editingNodeId);
                  return (node && (node.type || node.location_type));
                }
                return value.location_type || '';
              })()}
              onValueChange={(newValue) => {
                const editingNodeId = formData?._hierarchy_meta?.editing_node_id || formData?._hierarchy_meta?.target_chip_id || null;
                if (editingNodeId) {
                  const node = findNodeById(value, editingNodeId);
                  const isvalue = (node && (node.value || node.location_value));
                  const istpye = (node && (node.type || node.location_type));
                  if (newValue.trim().toLowerCase() && isvalue) {
                    isValidChildTreeTypeValue(newValue.trim().toLowerCase(), isvalue, "top")
                  }
                } else {
                  if (newValue.trim().toLowerCase() && value.location_value) {
                    isValidChildTreeTypeValue(newValue.trim().toLowerCase(), value.location_value, "top")
                  }
                }
                updatePrimaryLocation('location_type', newValue)
              }}
              placeholder="Type or select location type..."
              options={typesSuggestions}
              createLabel="Create type"
              disabled={disabled}
              className={`border-gray-300 px-3 py-2 text-sm font-medium `}
            />
            {/* {(selectedZone == null || (focusChipId && value.id == focusChipId)) &&
            <DuplicateChecker
              module="location_zones"
              value={(() => {
                const editingNodeId = formData?._hierarchy_meta?.editing_node_id || formData?._hierarchy_meta?.target_chip_id || null;
                if (editingNodeId) {
                  const node = findNodeById(value, editingNodeId);
                  return (node && (node.type || node.location_type)) || value.location_type || '';
                }
                return value.location_type || '';
              })()}
              excludeId={selectedZone?.id}
              onValidationChange={(data) =>  {
                setIsVailid(!data.isValid)
              }}
              // disabled={isUpdating}
            />
          } */}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Location Value <span className="text-red-500">*</span>
            </label>
            <Combobox
              value={(() => {
                const editingNodeId = formData?._hierarchy_meta?.editing_node_id || formData?._hierarchy_meta?.target_chip_id || null;
                if (editingNodeId) {
                  const node = findNodeById(value, editingNodeId);
                  return (node && (node.value || node.location_value));
                }
                return value.location_value || '';
              })()}
              onValueChange={(newValue) => {
                const editingNodeId = formData?._hierarchy_meta?.editing_node_id || formData?._hierarchy_meta?.target_chip_id || null;
                if (editingNodeId) {
                  const node = findNodeById(value, editingNodeId);
                  const isType = (node && (node.type || node.location_type));
                  const isvalue = (node && (node.value || node.location_value));
                  if (newValue.trim().toLowerCase() && isType) {
                    isValidChildTreeTypeValue(isType, newValue.trim().toLowerCase(), "top")
                  }
                } else {
                  if (newValue.trim().toLowerCase() && value.location_type) {
                    isValidChildTreeTypeValue(value.location_type, newValue.trim().toLowerCase(), "top")
                  }
                }
                updatePrimaryLocation('location_value', newValue)
              }}
              placeholder="Type or select location value..."
              options={valuesSuggestions}
              createLabel="Create value"
              disabled={disabled}
              className="border-gray-300"
            />
          </div>
          {
            isVailid &&
            <div className='mt-1'>
              <div className='flex items-start text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2'>
                <svg className="flex-shrink-0 mr-2 mt-0.5 h-4 w-4 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>
                  {`A location zone with this name and value already exists: "${(() => {
                    const nodeId =
                      formData?._hierarchy_meta?.editing_node_id ||
                      formData?._hierarchy_meta?.target_chip_id;

                    if (nodeId) {
                      const node = findNodeById(value, nodeId);
                      return node?.type || node?.location_type;
                    }

                    return value?.location_type;
                  })()
                    } : ${(() => {
                      const nodeId =
                        formData?._hierarchy_meta?.editing_node_id ||
                        formData?._hierarchy_meta?.target_chip_id;

                      if (nodeId) {
                        const node = findNodeById(value, nodeId);
                        return node?.value || node?.location_value;
                      }

                      return value?.location_value;
                    })()
                    }". Please choose a different name.`}
                </span>
              </div>
            </div>
          }
        </div>

        {/* Status and GPS — status bar sits full-width above the GPS map picker.
            The old "Set GPS" dropdown + coordinates readout were replaced by the
            map picker itself (search / drop a pin / manual coords). */}
        <div className="flex flex-col gap-3">
          <div className="flex gap-3 items-center">
            {/* update editing node if present, otherwise update root */}
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">Location Status</label>
              <select
                value={(() => {
                  const editingNodeId = formData?._hierarchy_meta?.editing_node_id || formData?._hierarchy_meta?.target_chip_id || null;
                  if (editingNodeId) {
                    const node = findNodeById(value, editingNodeId);
                    return (node && node.status) || value.status || 'normal';
                  }
                  return value.status || 'normal';
                })()}
                onChange={(e) => {
                  const editingNodeId = formData?._hierarchy_meta?.editing_node_id || formData?._hierarchy_meta?.target_chip_id || null;
                  const newStatus = e.target.value;
                  if (editingNodeId) {
                    updateNodeField(editingNodeId, 'status', newStatus);
                  } else {
                    // Update root and notify page which card should reflect this change.
                    onChange({ ...value, status: newStatus });
                  }
                }}
                disabled={disabled}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="normal">Normal</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="maintenance">Maintenance</option>
                <option value="emergency">Emergency</option>
                <option value="closed">Closed</option>
              </select>
            </div>


          </div>

          {/* GPS map picker (search or drop a pin) — replaces the old Set GPS button + coordinates readout */}
          {gpsPicker}
        </div>

        {/* media file select */}
        <div className="flex items-center gap-2">
          {/* Hidden file input */}
          <input
            type="file"
            id="incident-attachment-input"
            multiple
            disabled={disabled}
            onChange={handleFileChange}
            className="hidden"
          />

          {/* Custom button */}
          <button
            type="button"
            onClick={() =>
              document.getElementById("incident-attachment-input").click()
            }
            disabled={disabled}
            className="flex flex-1 justify-center items-center gap-2 border border-gray-300 rounded px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-50"
          >
            <Camera className="w-4 h-4" />
            Media
          </button>
        </div>

        {/* Preview Section */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-3">
            {attachments.map((item, index) => {
              const objectUrl = URL.createObjectURL(item);
              const isImage = item.type?.startsWith("image/");
              const isVideo = item.type?.startsWith("video/");
              const isPDF = (item.type || '').toLowerCase().includes('pdf') || item.type === 'application/pdf';
              const isDoc = /(msword|officedocument|presentation|spreadsheet|excel|sheet)/i.test(item.type || '');

              return (
                <div
                  key={index}
                  className="relative w-14 h-14 rounded-lg overflow-hidden border"
                >
                  {/* Image thumbnail */}
                  {isImage ? (
                    <img
                      src={objectUrl}
                      alt={item.name || 'preview'}
                      className="object-cover w-full h-full cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPreview({
                          type: item.type,
                          name: item.name,
                          size: item.size,
                          lastModified: item.lastModified,
                          src: objectUrl,
                          _isObjectURL: true
                        });
                      }}
                    />
                  ) : isVideo ? (
                    <video
                      src={objectUrl}
                      className="object-cover w-full h-full cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPreview({
                          type: item.type,
                          name: item.name,
                          size: item.size,
                          lastModified: item.lastModified,
                          src: objectUrl,
                          _isObjectURL: true
                        });
                      }}
                    // keep controls on the thumbnail for quick glance
                    />
                  ) : (
                    // PDF / Docs / other files: show icon tile and open modal on click
                    <div
                      className="w-full h-full flex items-center justify-center cursor-pointer bg-gray-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPreview({
                          type: item.type,
                          name: item.name || `file-${index}`,
                          size: item.size,
                          lastModified: item.lastModified,
                          src: objectUrl,
                          _isObjectURL: true
                        });
                      }}
                      title={item.name || 'Attachment'}
                    >
                      <div className="text-center text-gray-600">
                        <X className="hidden" />
                        <div className="mx-auto mb-0">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h6l4 4v6a2 2 0 01-2 2H7a2 2 0 01-2-2V7z" />
                          </svg>
                        </div>
                        <div className="text-xs mt-1 px-1">
                          {isPDF ? 'PDF' : isDoc ? 'DOC' : 'FILE'}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Remove Button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      // revoke object URL for removed file to free memory
                      try {
                        URL.revokeObjectURL(objectUrl);
                      } catch (err) {
                        /* ignore */
                      }
                      removeFile(index);
                    }}
                    className="absolute top-1 right-1 bg-black bg-opacity-50 text-white rounded-full p-1"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {/* If already added Preview Section */}
        {previewAttechment.length > 0 && (
          <div className="flex flex-wrap gap-3">
            {previewAttechment.map((item, index) => {
              const src = item.preview_url;
              const isImage = item.file_type?.startsWith("image/");
              const isVideo = item.file_type?.startsWith("video/");
              const isPDF = (item.file_type || '').toLowerCase().includes('pdf') || item.file_type === 'application/pdf';
              const isDoc = /(msword|officedocument|presentation|spreadsheet|excel|sheet)/i.test(item.file_type || '');

              return (
                <div
                  key={index}
                  className="relative w-14 h-14 rounded-lg overflow-hidden border"
                >
                  {isImage ? (
                    <AuthenticatedMedia
                      as="img"
                      src={src}
                      alt={item.name || item.key || 'preview'}
                      className="object-cover w-full h-full cursor-pointer"
                      onClick={() => {
                        setSelectedPreview({
                          type: item.file_type,
                          name: item.name || item.key || 'Attachment',
                          size: item.size,
                          lastModified: item.lastModified,
                          src,
                          _isObjectURL: false
                        });
                      }}
                    />
                  ) : isVideo ? (
                    <AuthenticatedMedia
                      as="video"
                      src={src}
                      className="object-cover w-full h-full cursor-pointer"
                      onClick={() => {
                        setSelectedPreview({
                          type: item.file_type,
                          name: item.name || item.key || 'Attachment',
                          size: item.size,
                          lastModified: item.lastModified,
                          src,
                          _isObjectURL: false
                        });
                      }}
                    />
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center cursor-pointer bg-gray-50"
                      onClick={() => {
                        setSelectedPreview({
                          type: item.file_type,
                          name: item.name || item.key || `attachment-${index}`,
                          size: item.size,
                          lastModified: item.lastModified,
                          src,
                          _isObjectURL: false
                        });
                      }}
                      title={item.name || item.key || 'Attachment'}
                    >
                      <div className="text-center text-gray-600">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h6l4 4v6a2 2 0 01-2 2H7a2 2 0 01-2-2V7z" />
                        </svg>
                        <div className="text-xs mt-1 px-1">
                          {isPDF ? 'PDF' : isDoc ? 'DOC' : 'FILE'}
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Remove Button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFileAllreadyAdded(item?.location_zone_id, item?.id);
                    }}
                    className="absolute top-1 right-1 bg-black bg-opacity-50 text-white rounded-full p-1"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Departments and Units */}
            <div>
              <label className="block text-sm font-medium my-1">
                Departments and Units
                <span className="text-xs text-gray-500 ml-1">(optional - leave blank for no department restrictions)</span>
              </label>

              <DepartmentSelector
                value={(() => {
                  const editingNodeId = formData?._hierarchy_meta?.editing_node_id || formData?._hierarchy_meta?.target_chip_id || null;
                  if (editingNodeId) {
                    const node = findNodeById(value, editingNodeId);
                    return (node && node.departments);
                  }
                  return value.departments || '';
                })()}
                onChange={(newValue) => {
                  console.log("object++++++++++++", newValue)
                  updatePrimaryLocation('departments', newValue)
                }}
                primaryIds={(() => {
                  const editingNodeId = formData?._hierarchy_meta?.editing_node_id || formData?._hierarchy_meta?.target_chip_id || null;
                  if (editingNodeId) {
                    const node = findNodeById(value, editingNodeId);
                    return (node && node.departments_primary) || [];
                  }
                  return value?.departments_primary || [];
                })()}
                onPrimaryToggle={handlePrimaryToggle}
                mode="multi"
                allowWildcard={false}
                placeholder="Select specific departments for this user"
                className="w-full"
                is_department_role={false}
              />
            </div>

        {/* Details & Sub-details Section */}
        <div className="space-y-4">
          <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            💡 Tip: Add details like Floor, Corridor, Room with unlimited nesting levels.
          </div>
          <div>
            {selectedZone == null || (focusChipId && value.id == focusChipId) ?
              <div>
                <label className="block text-sm font-medium mb-1">
                  Details <span className="text-xs text-gray-500 ml-2">(Optional - add as many as needed)</span>
                </label>

                {/* Add New Detail */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className='flex flex-col'>
                    <Combobox
                      value={newDetailInputs['detail_type'] || ''}
                      onValueChange={(value_is) => {
                        if (value_is.trim().toLowerCase() && newDetailInputs['detail_value']) {
                          const valueTypeArray = getAllTypesAndValues(value, false);

                          isValidChildTreeTypeValue(value_is, newDetailInputs['detail_value'], "middle", valueTypeArray)
                        }
                        handleDetailInputChange('detail_type', value_is);
                      }}
                      placeholder="Type"
                      options={typesSuggestions}
                      createLabel="Create type"
                      className="border-gray-300"
                    />

                  </div>


                  <div className="flex gap-2">
                    <Combobox
                      value={newDetailInputs['detail_value'] || ''}
                      onValueChange={(value_is) => {
                        if (value_is.trim().toLowerCase() && newDetailInputs['detail_type']) {
                          const valueTypeArray = getAllTypesAndValues(value, false);
                          isValidChildTreeTypeValue(newDetailInputs['detail_type'], value_is, "middle", valueTypeArray)
                        }
                        handleDetailInputChange('detail_value', value_is)

                      }}
                      placeholder="Value"
                      options={valuesSuggestions}
                      createLabel="Create value"
                      className="border-gray-300 flex-1"
                    />
                    <Button
                      onClick={addDetail}
                      disabled={!newDetailInputs['detail_type']?.trim() || !newDetailInputs['detail_value']?.trim() || disabled || isValidOptionalNode}
                      size="sm"
                      variant="secondary"
                      className="px-2 shrink-0"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {
                  isValidOptionalNode &&
                  <div className='mt-1'>
                    <div className='flex items-start text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2'>
                      <svg className="flex-shrink-0 mr-2 mt-0.5 h-4 w-4 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span>{`A location zones with this name and value already exists: "${newDetailInputs['detail_type']} : ${newDetailInputs['detail_value']}". Please choose a different name.`}</span>
                    </div>
                  </div>
                }
              </div> : <></>}

            {/* Display Details */}
            {value.details.length === 0 ? (
              <div className="text-center py-6 text-gray-500 border rounded-lg bg-gray-50">
                <p>No details added yet</p>
                <p className="text-xs">Add details like floors, corridors, or rooms above</p>
              </div>
            ) : (
              <div className="space-y-3">
                {value.details.map((detail, detailIndex) => {
                  const valueTypeArray = getAllTypesAndValues(detail, true);
                  const dataTress = valueTypeArray.concat([{ type: value.location_type.toLowerCase(), value: value.location_value, id: value.id }])
                  return (
                    renderDetail(detail, detailIndex, [], value.gps, value.departments, true, dataTress)
                  )
                }
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Attachment Preview Modal */}
      {selectedPreview && (
        <AttachmentPreviewModal
          attachment={{
            type: selectedPreview.type,
            name: selectedPreview.name,
            size: selectedPreview.size,
            lastModified: selectedPreview.lastModified,
            // AttachmentPreviewModal will use `src` (or preview_url) to render content
            src: selectedPreview.src || selectedPreview.preview_url || selectedPreview.url,
            isObjectURL: selectedPreview._isObjectURL
          }}
          onClose={() => {
            // Revoke object URL if we generated one for local File previews
            if (selectedPreview?._isObjectURL && selectedPreview.src) {
              try {
                URL.revokeObjectURL(selectedPreview.src);
              } catch (err) {
                /* ignore */
              }
            }
            setSelectedPreview(null);
          }}
        />
      )}
    </>
  );
};

export default LocationHierarchyManager;
